import { useState } from "react";
import axios from "axios";
import { Search, TrendingUp, Lightbulb, Loader2, ExternalLink, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function KeywordResearchTool() {
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const research = async (e) => {
    e?.preventDefault();
    if (!phrase.trim()) return;
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/keyword/research`, {
        params: { phrase: phrase.trim() },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Błąd");
    } finally { setLoading(false); }
  };

  const trends = data?.google_trends;

  return (
    <div className="p-6 max-w-none mx-auto">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
        <Search className="w-7 h-7 text-blue-600" /> Keyword Research
      </h1>
      <p className="text-sm text-slate-500 mb-4">
        Autocomplete · People Also Ask · Related Searches · Google Trends — wszystko z free APIs (bez SEMSTORM).
      </p>

      <form onSubmit={research} className="flex gap-2 mb-6">
        <input value={phrase} onChange={e => setPhrase(e.target.value)}
          placeholder="np. kredyt hipoteczny, skarga na urzędnika, sex anonse Warszawa…"
          className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none" />
        <button type="submit" disabled={loading}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Szukaj
        </button>
      </form>
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm mb-4">{error}</div>}

      {data?.ok && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Trends */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-bold flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-purple-600" /> Google Trends 12m</h2>
            {trends?.ok ? (
              <>
                <div className="text-3xl font-bold text-purple-700">{trends.average_interest}/100</div>
                <div className="text-xs text-slate-500">średnie zainteresowanie</div>
                <div className="mt-3 text-sm">
                  <div>Trend 30d: <span className={trends.trend_pct >= 0 ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{trends.trend_pct > 0 ? "+" : ""}{trends.trend_pct}%</span></div>
                  <div>Max: {trends.max_interest}</div>
                </div>
                {trends.rising_related?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Rosnące zapytania</div>
                    {trends.rising_related.slice(0, 5).map((r, i) => (
                      <div key={i} className="text-xs text-slate-700">↑ {r.query} <span className="text-green-600">{r.value}%</span></div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-slate-500">{trends?.message || trends?.error || "Brak danych Trends"}</div>
            )}
          </div>

          {/* People Also Ask */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-bold flex items-center gap-2 mb-3"><Lightbulb className="w-5 h-5 text-amber-600" /> Pytania użytkowników</h2>
            {data.people_also_ask?.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {data.people_also_ask.slice(0, 10).map((q, i) => (
                  <li key={i} className="text-slate-700 leading-snug">
                    <span className="text-amber-500">▸</span> {q}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">Brak pytań PAA</div>
            )}
          </div>

          {/* Autocomplete */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-bold flex items-center gap-2 mb-3">📝 Autocomplete ({data.autocomplete?.length || 0})</h2>
            <ul className="space-y-1 text-sm max-h-96 overflow-y-auto">
              {(data.autocomplete || []).map((s, i) => (
                <li key={i} className="text-slate-700">
                  <button onClick={() => { setPhrase(s); research(); }}
                    className="text-left hover:bg-slate-50 rounded px-2 py-0.5 w-full">
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 text-sm">
          <strong>Co dostaniesz:</strong>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Google Trends — średnie zainteresowanie 12 miesięcy + trend 30d + powiązane rosnące</li>
            <li>People Also Ask — pytania użytkowników z SERP (długi ogon)</li>
            <li>Autocomplete — 30+ sugestii z różnymi początkami</li>
            <li>Related Searches — powiązane wyszukiwania</li>
          </ul>
        </div>
      )}
    </div>
  );
}
